const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || (typeof window !== 'undefined' && (window as any).opera) || '';

function detectIOS() {
	return /iPad|iPhone|iPod/.test(ua);
}

function detectAndroid() {
	return /Android/.test(ua);
}

function detectIOSSafari() {
	return detectIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function detectIOSChrome() {
	return detectIOS() && /CriOS/.test(ua);
}

function detectAndroidChrome() {
	return detectAndroid() && /Chrome/.test(ua) && !/SamsungBrowser/.test(ua);
}

function detectSamsungBrowser() {
	return /SamsungBrowser/.test(ua);
}

function detectPWAInstalled() {
	if (typeof window === 'undefined') return false;
	return window.matchMedia('(display-mode: standalone)').matches
		|| (window.navigator as any).standalone === true;
}

function detectIOSWebView() {
	return detectIOS() && /AppleWebKit/.test(ua) && !/Safari/.test(ua);
}

function detectAndroidWebView() {
	return /Android.*wv/.test(ua) || /Version\/[\d.]+.*Chrome/.test(ua);
}

function detectTrustedWebActivity() {
	if (typeof document === 'undefined') return false;
	return document.referrer.startsWith('android-app://');
}

function detectNativeBridge() {
	if (typeof window === 'undefined') return false;
	return !!(
		(window as any).ReactNativeWebView ||
		(window as any).webkit?.messageHandlers
	);
}

function detectDebugWebView() {
	if (typeof window === 'undefined') return false;

	const params = new URLSearchParams(window.location.search);
	const device = params.get('device')?.toLowerCase();

	if (device === 'webview') {
		try { localStorage.setItem('debug_webview', '1'); } catch { /* ignore */ }
		return true;
	}
	if (device === 'browser') {
		try { localStorage.removeItem('debug_webview'); } catch { /* ignore */ }
		return false;
	}

	return (
		params.get('webview') === '1' ||
		localStorage.getItem('debug_webview') === '1'
	);
}

function detectSafeArea() {
	if (typeof document === 'undefined') return false;
	const style = getComputedStyle(document.documentElement);
	const top = style.getPropertyValue('env(safe-area-inset-top)');
	return !!top && top !== '0px';
}

export interface DeviceInfo {
	isIOS: boolean;
	isAndroid: boolean;
	isMobile: boolean;
	isIOSSafari: boolean;
	isIOSChrome: boolean;
	isAndroidChrome: boolean;
	isSamsungBrowser: boolean;
	isWebView: boolean;
	isIOSWebView: boolean;
	isAndroidWebView: boolean;
	isTWA: boolean;
	isPWA: boolean;
	hasSafeArea: boolean;
	isBrowser: boolean;
}

export function getDeviceInfo(): DeviceInfo {
	const isIOS = detectIOS();
	const isAndroid = detectAndroid();
	const isIOSWebView = detectIOSWebView();
	const isAndroidWebView = detectAndroidWebView();
	const isPWA = detectPWAInstalled();
	const isWebView = isIOSWebView || isAndroidWebView || detectNativeBridge() || detectDebugWebView();

	return {
		isIOS,
		isAndroid,
		isMobile: isIOS || isAndroid,
		isIOSSafari: detectIOSSafari(),
		isIOSChrome: detectIOSChrome(),
		isAndroidChrome: detectAndroidChrome(),
		isSamsungBrowser: detectSamsungBrowser(),
		isWebView,
		isIOSWebView,
		isAndroidWebView,
		isTWA: detectTrustedWebActivity(),
		isPWA,
		hasSafeArea: detectSafeArea(),
		isBrowser: !isWebView && !isPWA,
	};
}

export const device = getDeviceInfo();
